// THE GENERATED BOARD — the assistant's answer as a VIEW of the home grid area
// (DESIGN-WL-ASSISTANT-MAIN-VIEW-1, ASSISTANT_BOARD_MODE_SPEC.md §4–§5). Nika,
// 2026-09-26: "ai assistant can directly render in main dashboard. it can make
// generative dashboards using all apps installed in workspace on fly."
//
// It REPLACES the grid area with a crossfade — the saved board is untouched
// underneath and returns on Esc, Dismiss or the "Home" segment; nothing on the
// page moves (Rule 66: the view keeps its box while closed, only opacity and
// visibility change). Every value it paints comes from functions/
// assistantBoard.js through `el.call` — the component holds no logic of its own:
//
//   head      the board's title (the greeting type; `tabindex -1`, focused
//             after generation) · the summary, facts in `title` ink, a polite
//             live region · the micro-label "GENERATED · 3 WIDGETS · JUST NOW"
//             (never the app list) · Pin all · Save as board · ONE square
//             Dismiss (brand CloseButton) · the status line (a failure + Try
//             again, an empty board, a pin/save notice)
//   refine    the model's refine suggestions as kit capsules
//   grid      ws-home's metrics (functions/homeGridMetrics.js): square cells,
//             sizes S · M · L · XL spanning by the column count, cells stamped
//             `data-size` + `data-widget-size` (the size vocabulary the home
//             slot components key on) — skeletons first, at their final size
//
// Cards (AssistantBoardCard): a WIDGET card mounts the installed app's own home
// body with its `params`, under the module's design-system context; a BLOCK
// card draws the AI-widget block interpreters (WidgetRenderer / AiWidgetBlock)
// and says where its numbers came from: "AI · FROM BI REVENUE". Both skins and
// both themes come from the tokens alone — nothing here reads the skin.
//
// Mounted by pages/main.js (docs/patches/assistant-board-mount.main.js.patch,
// ws-home's fence); shown in Page mode on the home — the answers set to Page
// (`assistantDock === 'main'`) while the board service answers
// (`_aiBoardsAvailable`, which this view's mount asks) — or under the
// `_aiBoardPreview` UI mode. The composer's send reaches
// `assistantBoardGenerate` in the same mode (functions/homeAiSend.js).
export const AssistantBoardView = {
  tag: 'section',
  role: 'region',
  ariaLabel: (el) => el.call('polyglot', 'workspace.assistantBoard.region'),
  // The measured grid metrics — geometry of THIS element, so its own state,
  // never root state. 0 = not measured yet (the widest table step applies).
  state: { cols: 0, gutter: 0, cell: 0, square: true },

  dataOpen: (el, s) => (el.call('assistantBoardShown', s.root) ? 'true' : 'false'),
  dataStatus: (el, s) => {
    const vm = el.call('assistantBoardViewModel', s.root)
    return (vm && vm.status) || 'idle'
  },
  ariaHidden: (el, s) => (el.call('assistantBoardShown', s.root) ? null : 'true'),
  ariaBusy: (el, s) => {
    const vm = el.call('assistantBoardViewModel', s.root)
    return vm && (vm.status === 'loading' || vm.status === 'streaming') ? 'true' : null
  },

  display: 'flex',
  flexDirection: 'column',
  gap: 'B',
  // ONE column with the board (DESIGN-WL-HOME-WIDTH-RESIZE-1 (a)): the head,
  // the actions, the refine chips and the grid all sit on the home grid's own
  // width (`--home-grid-w`, the board stylesheet's — pages/main.js
  // _injectWidgetStyles), centred. Measured before: the head started at the
  // page edge and the actions ended at the other while the cards sat in the
  // centred column (2560 px, dev 8f18b1cb4). Outside the board (no variable)
  // the view keeps its full width.
  width: '100%',
  maxWidth: 'var(--home-grid-w, 100%)',
  marginInline: 'auto',
  minWidth: '0',
  boxSizing: 'border-box',
  // CLEAR OF THE COMPOSER'S STACK (fly-onboarding, ai-nino 2026-10-28: the
  // saved board's name chip covered its last cards). On the home the board
  // lies over the grid OUT of the flow (absolute), so the page column's end
  // padding never reaches it: a scroller's padding follows its in-flow
  // content only (measured in Chrome: a 1000px absolute child of a scroller
  // with 100px end padding scrolls to 1000). Its own bottom inset is the
  // shell's bottom line (`--slot-bottom`: the composer, its docked rows, a
  // toast while one shows, + the gap), the phone chip row (`--chip-row`, 0
  // off phones) and one spacing step. Over a page the layer's band already
  // ends above the stack (components/AssistantMainLayer.js zeroes this).
  paddingBottom: 'calc(var(--slot-bottom) + var(--chip-row) + var(--spacing-A))',
  // brought to the top as a new board (below), it stays clear of the shell's
  // floating header — the Fly page's own margin
  scrollMarginTop: 'calc(var(--shell-top) + var(--spacing-A))',
  // The crossfade (spec §5): `C`, opacity only. Closed keeps the box and hides
  // after the fade; open shows at once and fades in. A static rule on the
  // `data-open` stamp — never `hide:` (display none cannot transition, and the
  // grid behind it must not move).
  opacity: '0',
  visibility: 'hidden',
  pointerEvents: 'none',
  transition: 'opacity C defaultBezier, visibility 0s linear C',
  '&[data-open="true"]': {
    opacity: '1',
    visibility: 'visible',
    pointerEvents: 'auto',
    transition: 'opacity C defaultBezier, visibility 0s linear 0s'
  },

  // Mounted: measure the grid, ask whether Page mode exists in this workspace
  // (`_aiBoardsAvailable`), list its saved boards — and again after a
  // workspace switch (`subscribeTo` re-runs onUpdate; the function acts once
  // per workspace). Esc and P join the shell keyboard registry while the view
  // is on the page — registered on mount, taken out on remove
  // (functions/assistantBoard.js assistantBoardShortcuts).
  // The installed apps publishing their widgets (`workspaceApps`,
  // `workspaceProject`) re-derive the board on screen: a cold `?board=` load
  // shows it before they land (qa round 11 F1, assistantBoardRederive — no
  // write unless its rows changed). The project list and the App Store list
  // name the links of the site cards (a row's ↗), and they too can land after
  // the board (MISSING-NAMES-2).
  subscribeTo: [
    'workspace',
    'workspaceApps',
    'workspaceProject',
    '_projectsForActiveOrg',
    '_wsAppsCatalog'
  ],
  onMounted: (el, s) => {
    el.call('assistantBoardMounted', el, s)
    el.call('assistantBoardShortcuts', el)
  },
  onUpdate: (el, s) => {
    el.call('assistantBoardMounted', el, s)
    el.call('assistantBoardRederive', s && s.root)
  },
  onWindowResize: (e, el, s) => el.call('assistantBoardSyncGrid', el, s),
  onRemove: (el) => {
    if (typeof el.__boardShortcutsOff === 'function') el.__boardShortcutsOff()
    el.call('assistantBoardGridReadOff', el)
  },
  // A NEW BOARD — an ask, a refine, a saved board opened — comes to the top of
  // the view, once per request (Nika 2026-10-23: "when answering in new
  // section always scroll top of that section"; functions/assistantReveal.js).
  // A board shorter than the scroller takes the room it needs (its
  // `minHeight`), while it is on screen.
  minHeight: (el, s) =>
    el.call('assistantRevealRoom', el, s.root, el.call('assistantBoardShown', s.root)),
  stateDeps: [
    (el, s) => {
      const b = s.root && s.root._aiBoard
      return b && (b.requestId || b.id) ? String(b.requestId || b.id) : null
    }
  ],
  onStateUpdate: (el, s, ctx, change) => {
    const next = ((change && change.next) || [])[0]
    if (next && el.call('assistantBoardShown', s.root)) {
      el.call('assistantRevealTop', el, null, 'board' + next)
    }
  },

  BoardHead: {
    flow: 'y',
    gap: 'Z',
    minWidth: '0',

    BoardHeading: {
      flow: 'x',
      align: 'flex-start',
      gap: 'A',
      minWidth: '0',
      BoardIdentity: {
        flow: 'y',
        gap: 'Z',
        minWidth: '0',
        flex: '1',
        // The greeting line's type (HOME_WIDGETS_SPEC §4.1): `C` 700, `title`.
        // The title never truncates — it wraps (HOME_AMBIENT_SKIN_SPEC §3b).
        BoardTitle: {
          tag: 'h2',
          dataBoardTitle: true,
          tabindex: '-1',
          hide: (el, s) => {
            const vm = el.call('assistantBoardViewModel', s.root)
            return !!(vm && vm.rename)
          },
          margin: '0',
          fontSize: 'C',
          '@mobileL': { fontSize: 'B' },
          '@appMobile': { fontSize: 'B' },
          fontWeight: '700',
          lineHeight: '1.2',
          color: 'title',
          minWidth: '0',
          overflowWrap: 'anywhere',
          // The title takes focus after a board lands (spec §5) so a screen reader
          // reads from it — a reading position, not a control: `tabindex -1`,
          // never in the tab order, so no ring either. A programmatic focus after
          // a keystroke counts as :focus-visible, and the ring used to paint the
          // whole width after every generation.
          ':focus': { outline: 'none' },
          ':focus-visible': { outline: 'none', boxShadow: 'none' },

          TitleText: {
            tag: 'span',
            text: (el, s) => {
              const vm = el.call('assistantBoardViewModel', s.root)
              return (vm && vm.spec && vm.spec.title) || ''
            }
          },
          // While the title is on its way: an inline bar one line box tall, so the
          // head keeps its height when the words land.
          Skeleton: {
            display: 'inline-block',
            verticalAlign: 'middle',
            width: 'F',
            height: '1em',
            hide: (el, s) => {
              const vm = el.call('assistantBoardViewModel', s.root)
              return !vm || !!(vm.spec && vm.spec.title) || vm.status === 'failed'
            }
          }
        },

        // RENAME (D4): the title as the kit field, the same line; Enter or Save
        // keeps the name, Esc or Cancel leaves it (Esc stays in the field — the
        // board's own Esc does not close the view from here).
        BoardRename: {
          if: (el, s) => {
            const vm = el.call('assistantBoardViewModel', s.root)
            return !!(vm && vm.rename)
          },
          flow: 'x',
          flexWrap: 'wrap',
          align: 'center',
          gap: 'Y',
          minWidth: '0',
          AppInput: {
            dataBoardRenameField: true,
            maxlength: '60',
            width: 'auto',
            flex: '1',
            minWidth: 'F',
            ariaLabel: (el) => el.call('polyglot', 'workspace.assistantBoard.renameField'),
            value: (el, s) => {
              const vm = el.call('assistantBoardViewModel', s.root)
              return (vm && vm.rename && vm.rename.draft) || ''
            },
            onRender: (el) => {
              if (el.node && typeof el.node.focus === 'function') el.node.focus()
            },
            onInput: (e, el) => el.call('assistantBoardRenameDraft', e.target.value),
            onKeydown: (e, el) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                el.call('assistantBoardRename')
              } else if (e.key === 'Escape') {
                e.preventDefault()
                e.stopPropagation()
                el.call('assistantBoardRenameCancel')
              }
            }
          },
          AppCapsuleBtnPrimary: {
            compact: true,
            dataBoardRenameSave: true,
            text: '{{ common.action.save | polyglot }}',
            onClick: (e, el) => el.call('assistantBoardRename')
          },
          AppCapsuleBtnToolbar: {
            compact: true,
            dataBoardRenameCancel: true,
            text: '{{ common.action.cancel | polyglot }}',
            onClick: (e, el) => el.call('assistantBoardRenameCancel')
          }
        }
      },
      // The ONE square button of the head: 1:1, the brand close. Its name is
      // this view's verb, not the brand's default "Close": the brand declares
      // that default in its `attr` block, and a flat `ariaLabel` beside it
      // races it (measured: the brand's word won) — only the same `attr` key
      // replaces it, by the shallow merge `attr` blocks get.
      CloseButton: {
        flexShrink: '0',
        alignSelf: 'flex-start',
        dataBoardDismiss: true,
        attr: { 'aria-label': (el) => el.call('polyglot', 'workspace.assistantBoard.dismiss') },
        onClick: (e, el) => el.call('assistantBoardDismiss', { focusComposer: true })
      }
    },

    // Facts in `title` 500, the words between in `inactive` (spec §4); the
    // live region exists before its content changes, so the answer is read.
    BoardSummary: {
      tag: 'p',
      dataBoardSummary: true,
      ariaLive: 'polite',
      margin: '0',
      fontSize: 'A',
      lineHeight: '1.45',
      maxWidth: 'H',
      minWidth: '0',
      overflowWrap: 'anywhere',
      // CLS 0 (spec §6.1): the head holds TWO lines of summary from the first
      // paint, so the cards do not fall when the sentences land. Measured on
      // dev 2026-10-02: the busy head was 33 px and the complete head 146 px,
      // and all three cards dropped dy +113 px at that moment. `2.9em` is
      // 2 × this element's own `lineHeight` 1.45, so the reservation tracks the
      // type rather than a pixel. The clamp is the other half of the same law —
      // a summary longer than two lines is cut INSIDE the reservation instead
      // of growing past it. Both from answer A.
      minHeight: (el, s) =>
        el.call('assistantBoardSummaryReserved', el.call('assistantBoardViewModel', s.root))
          ? '2.9em'
          : '0',
      lineClamp: '2',
      children: (el, s) =>
        el.call('assistantBoardSummaryRows', el.call('assistantBoardViewModel', s.root)),
      childrenAs: 'state',
      childExtends: 'AssistantBoardFact'
    },

    BoardBar: {
      flow: 'y',
      align: 'stretch',
      gap: 'Z',
      minWidth: '0',

      AppMicroLabel: {
        dataBoardMicro: true,
        whiteSpace: 'normal',
        text: (el, s) => el.call('assistantBoardMicro', el.call('assistantBoardViewModel', s.root))
      },

      BoardActions: {
        minWidth: '0',
        maxWidth: '100%',
        width: '100%',
        flow: 'x',
        flexWrap: 'wrap',
        align: 'center',
        gap: 'Y',

        AppCapsuleBtnPrimary: {
          compact: true,
          dataBoardPinAll: true,
          disabled: (el, s) =>
            el.call('assistantBoardActionsIdle', el.call('assistantBoardViewModel', s.root)) ||
            null,
          onClick: (e, el, s) => el.call('pinAllBoardItems', el, s),
          AppLabelStack: {
            labels: [
              'workspace.assistantBoard.pinAll',
              'workspace.assistantBoard.pinning',
              'workspace.assistantBoard.pinnedAll'
            ],
            shown: (el, s) =>
              el.call('assistantBoardPinAllLabel', el.call('assistantBoardViewModel', s.root))
          }
        },

        AppCapsuleBtnToolbar: {
          compact: true,
          dataBoardSave: true,
          disabled: (el, s) =>
            el.call('assistantBoardActionsIdle', el.call('assistantBoardViewModel', s.root)) ||
            null,
          onClick: (e, el, s) => el.call('saveBoard', el, s),
          AppLabelStack: {
            labels: [
              'workspace.assistantBoard.saveAsBoard',
              'workspace.assistantBoard.saving',
              'workspace.assistantBoard.saved'
            ],
            shown: (el, s) =>
              el.call('assistantBoardSaveLabel', el.call('assistantBoardViewModel', s.root))
          }
        },

        // REFRESH DATA (D3; Pulse's Refresh): every records card reads again
        // and every app widget body re-mounts. Rests while a card is reading.
        AppCapsuleBtnToolbar_refresh: {
          compact: true,
          dataBoardRefresh: true,
          disabled: (el, s) =>
            el.call('assistantBoardActionsIdle', el.call('assistantBoardViewModel', s.root)) ||
            el.call('assistantBoardRefreshing', s.root) ||
            null,
          text: '{{ workspace.assistantBoard.refresh | polyglot }}',
          onClick: (e, el) => el.call('assistantBoardRefresh')
        },

        // SHARE — the record's link for the workspace (saved first when it is
        // not yet a record) — and OPEN IN SIDEBAR — the board moves to the
        // docked panel's thread (functions/assistantBoard.js).
        AppCapsuleBtnToolbar_share: {
          compact: true,
          dataBoardShare: true,
          disabled: (el, s) =>
            el.call('assistantBoardActionsIdle', el.call('assistantBoardViewModel', s.root)) ||
            null,
          onClick: (e, el, s) => el.call('assistantBoardShare', el, s),
          AppLabelStack: {
            labels: [
              'workspace.assistantBoard.share',
              'workspace.assistantBoard.sharing',
              'workspace.assistantBoard.shared'
            ],
            shown: (el, s) =>
              el.call('assistantBoardShareLabel', el.call('assistantBoardViewModel', s.root))
          }
        },
        AppCapsuleBtnToolbar_sidebar: {
          compact: true,
          dataBoardToSidebar: true,
          disabled: (el, s) =>
            el.call('assistantBoardActionsIdle', el.call('assistantBoardViewModel', s.root)) ||
            null,
          text: '{{ workspace.assistantThread.openInSidebar | polyglot }}',
          onClick: (e, el) => el.call('assistantBoardToSidebar')
        },

        BoardManage: {
          flow: 'x',
          flexWrap: 'wrap',
          align: 'center',
          gap: 'Y',
          minWidth: '0',
          maxWidth: '100%',
          marginLeft: 'auto',
          '@mobileL': { width: '100%', marginLeft: '0' },
          '@appMobile': { width: '100%', marginLeft: '0' },
          // SAVED BOARDS (D4; Pulse's boards store): Rename (a saved board the
          // viewer may edit, or any unsaved one), Duplicate and Delete (a saved
          // record; Delete asks first through the house confirm).
          AppCapsuleBtnGhost_rename: {
            compact: true,
            dataBoardRename: true,
            if: (el, s) => el.call('assistantBoardManage', s.root).canRename,
            text: '{{ common.action.rename | polyglot }}',
            onClick: (e, el) => el.call('assistantBoardRenameStart')
          },
          AppCapsuleBtnGhost_duplicate: {
            compact: true,
            dataBoardDuplicate: true,
            if: (el, s) => el.call('assistantBoardManage', s.root).canDuplicate,
            text: '{{ common.action.duplicate | polyglot }}',
            onClick: (e, el) => el.call('assistantBoardDuplicate')
          },
          AppCapsuleBtnGhost_delete: {
            compact: true,
            dataBoardDelete: true,
            if: (el, s) => el.call('assistantBoardManage', s.root).canDelete,
            text: '{{ common.action.delete | polyglot }}',
            onClick: (e, el) => el.call('assistantBoardDeleteAsk')
          }
        }
      }
    },

    // A failure (with Try again), an empty board, the last pin / save notice.
    // Always mounted — a status region must exist before it speaks.
    BoardStatus: {
      dataBoardStatus: true,
      role: 'status',
      flow: 'x',
      flexWrap: 'wrap',
      align: 'center',
      gap: 'Z',
      minWidth: '0',

      StatusText: {
        tag: 'span',
        dataBoardStatusText: true,
        fontSize: 'A',
        fontWeight: '500',
        color: 'title',
        minWidth: '0',
        overflowWrap: 'anywhere',
        text: (el, s) => el.call('assistantBoardStatus', el.call('assistantBoardViewModel', s.root))
      },

      AppCapsuleBtnToolbar: {
        compact: true,
        dataBoardRetry: true,
        hide: (el, s) => {
          const vm = el.call('assistantBoardViewModel', s.root)
          return !vm || vm.status !== 'failed'
        },
        text: '{{ workspace.assistantBoard.tryAgain | polyglot }}',
        onClick: (e, el) => el.call('assistantBoardRetry')
      }
    }
  },

  // THE PERIOD (D3; Pulse's window): the kit's segmented choice over the
  // six records presets. The board's one shared period is chosen (none when
  // the cards differ); a choice re-asks every dated records card. Only on a
  // ready board with a dated records card.
  AppSegmented: {
    dataBoardPeriod: true,
    if: (el, s) => el.call('assistantBoardPeriodShown', el.call('assistantBoardViewModel', s.root)),
    variant: 'choice',
    compact: true,
    ariaLabel: (el) => el.call('polyglot', 'workspace.assistantBoard.period'),
    current: (el, s) => el.call('assistantBoardPeriod', el.call('assistantBoardViewModel', s.root)),
    select: 'assistantBoardSetPeriod',
    childrenAs: 'state',
    children: (el) => el.call('assistantBoardPeriodOptions')
  },

  // The model's refine suggestions — one tap asks again, against this board.
  BoardRefine: {
    dataBoardRefine: true,
    role: 'group',
    ariaLabel: (el) => el.call('polyglot', 'workspace.assistantBoard.refine'),
    flow: 'x',
    flexWrap: 'wrap',
    gap: 'Y',
    minWidth: '0',
    // CLS 0 (spec §6.1): the group holds ONE chip row from the first paint, so
    // the grid does not fall when the suggestions land. Measured on dev
    // 2026-10-02: the landed cards dropped a further dy +36 px, which is
    // exactly `controlHCompact` — one compact capsule, what these chips are.
    // Reserved only while chips are coming or present (answer A).
    minHeight: (el, s) =>
      el.call('assistantBoardRefineReserved', el.call('assistantBoardViewModel', s.root))
        ? 'controlHCompact'
        : '0',
    children: (el, s) => {
      const vm = el.call('assistantBoardViewModel', s.root)
      return ((vm && vm.spec && vm.spec.refine) || []).map((r, i) => ({ key: 'refine' + i, ...r }))
    },
    childrenAs: 'state',
    childExtends: 'AppCapsuleBtnToolbar',
    childProps: {
      compact: true,
      whiteSpace: 'normal',
      text: (el, s) => (s && s.label) || '',
      onClick: (e, el, s) => el.call('assistantBoardRefine', { label: s.label, prompt: s.prompt })
    }
  },

  BoardGrid: {
    dataBoardGrid: true,
    display: 'grid',
    gridAutoFlow: 'row dense',
    justifyContent: 'center',
    minWidth: '0',
    style: (el, s) => el.call('assistantBoardGridStyle', s),
    children: (el, s) =>
      el.call(
        'assistantBoardCells',
        el.call('assistantBoardViewModel', s.root),
        el.call('assistantBoardMetrics', s).cols
      ),
    childrenAs: 'state',
    childExtends: 'AssistantBoardCard'
  }
}

// One run of the summary: a fact (`title` 500) or the words between
// (`inactive`). Guards `s` — a childExtends template evaluates once unbound.
export const AssistantBoardFact = {
  tag: 'span',
  dataFact: (el, s) => (s && s.fact ? 'true' : null),
  color: (el, s) => (s && s.fact ? 'title' : 'inactive'),
  fontWeight: (el, s) => (s && s.fact ? '500' : '400'),
  text: (el, s) => (s && s.text) || ''
}

// One generated card: the home widget anatomy (HOME_WIDGETS_SPEC §2) — plate,
// title, the ONE square button (here: Pin), then the body. A skeleton card
// holds the same geometry until its item lands (CLS 0). Surface, radius and
// padding are the home card's; the hover is an inset wash, nothing moves.
//
// A skeleton creates ONLY what it paints (DESIGN-WL-ASSISTANT-BOARD-SUBMIT-
// BLOCKS-THE-MAIN-THREAD-FOR-A-SECOND-1, answer B): the chrome a given `kind`
// never shows is `if:`-parked — no node, no subtree — not `hide:`-hidden,
// because building it was most of the submit task once the layout reads left.
// Safe because a cell's KEY changes when its item lands (`skeleton-N` → the
// item's key), so the real card is created fresh with its chrome; `hide:`
// stays only where one card flips in place (a ui card's read, a block's
// empty verdict).
export const AssistantBoardCard = {
  tag: 'article',
  dataBoardItem: (el, s) => (s && s.key) || null,
  dataKind: (el, s) => (s && s.kind) || null,
  dataSize: (el, s) => (s && s.size) || 's',
  dataWidgetSize: (el, s) => (s && s.size) || 's',
  ariaBusy: (el, s) => (s && s.kind === 'skeleton' ? 'true' : null),
  style: (el, s) => el.call('assistantBoardCardStyle', s, el.context && el.context.designSystem),

  // THE ACCENT SELECTOR — the same two lines as the home frame
  // (components/HomeWidgetFrame.js), and the same reason: the style above
  // publishes both halves of the on-surface accent because the DECISION is
  // theme-dependent, and only a `[data-theme]` rule follows a theme flip
  // without a re-render. Goes when this view mounts through ws-home's frame,
  // together with `assistantBoardHueVars`.
  '--home-widget-accent': 'var(--home-widget-accent-light)',
  '@dark': { '--home-widget-accent': 'var(--home-widget-accent-dark)' },

  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: 'Z',
  minWidth: '0',
  minHeight: '0',
  boxSizing: 'border-box',
  padding: 'A',
  fontSize: 'A',
  round: 'B1',
  background: 'var(--home-card-bg, var(--theme-card-background))',
  color: 'title',

  // First paint: one fade per card, staggered by position — opacity only.
  // The timing scale has no step below Z, so the 40 ms stagger is literal
  // (HOME_WIDGETS_SPEC §5); the brand reset lands it at once under reduced
  // motion.
  animation: 'fadeIn',
  animationDuration: 'C',
  animationTimingFunction: 'defaultBezier',
  animationFillMode: 'backwards',
  animationDelay: (el, s) => Math.min((s && s.index) || 0, 8) * 40 + 'ms',

  // the wash lies under the card's words (its own stacking context, the layer
  // at -1): a hover darkens the fill, never the text — nothing in a board card
  // paints over its neighbours
  isolation: 'isolate',
  '::after': {
    content: '""',
    position: 'absolute',
    inset: '0',
    zIndex: '-1',
    borderRadius: 'inherit',
    background: 'cardHover',
    opacity: '0',
    pointerEvents: 'none',
    transition: 'opacity A defaultBezier'
  },
  ':hover::after': { opacity: '1' },

  CardHead: {
    flow: 'x',
    align: 'center',
    gap: 'Z',
    minWidth: '0',

    HomeWidgetPlate: {
      if: (el, s) => !!s && s.kind !== 'skeleton',
      Icon: { name: (el, s) => (s && s.icon) || 'star' }
    },
    SkeletonCircle: {
      if: (el, s) => !!s && s.kind === 'skeleton',
      boxSize: 'iconXl',
      minWidth: 'iconXl'
    },

    // Primary text: it wraps and is never cut — no clamp, no ellipsis
    // (HOME_AMBIENT_SKIN_SPEC §3 / §3b); validation caps a title at 60
    // characters, so two lines hold it at every cell width.
    CardTitle: {
      tag: 'h3',
      dataBoardCardTitle: true,
      if: (el, s) => !!s && s.kind !== 'skeleton',
      flex: '1',
      margin: '0',
      minWidth: '0',
      fontSize: 'Z',
      fontWeight: '500',
      lineHeight: '1.3',
      color: 'title',
      overflowWrap: 'anywhere',
      text: (el, s) => (s && s.title ? el.call('polyglot', s.title) : '')
    },
    SkeletonRow: {
      if: (el, s) => !!s && s.kind === 'skeleton',
      width: '50%'
    },

    // The card's ONE square button = Pin (spec §4): 1:1, named by its verb and
    // the card's title; a card pinned (or on a preview board) is done.
    HomeWidgetAction: {
      dataBoardPin: true,
      // A records card pins too (WIDGET-TOOLS-RECORDS-SHAPE-1); a skeleton
      // has nothing to pin.
      if: (el, s) => !!s && s.kind !== 'skeleton',
      disabled: (el, s) => {
        const vm = el.call('assistantBoardViewModel', s && s.root)
        const state = el.call('assistantBoardPinState', s && s.root, s && s.key)
        return !vm ||
          vm.preview ||
          vm.status !== 'ready' ||
          state === 'pinned' ||
          state === 'pinning'
          ? true
          : null
      },
      ariaLabel: (el, s) =>
        el.call(
          'assistantBoardPinLabel',
          s,
          el.call('assistantBoardPinState', s && s.root, s && s.key) === 'pinned'
        ),
      onClick: (e, el, s) => {
        e.stopPropagation()
        el.call('pinBoardItem', el, s, s && s.parse ? s.parse() : s)
      },
      Icon: {
        name: (el, s) =>
          el.call('assistantBoardPinState', s && s.root, s && s.key) === 'pinned'
            ? 'pin'
            : 'pinOutline'
      }
    },

    // A DRILL card's ONE square button is Close (D2 drill-down): the card
    // leaves the board, its source stays. A records card has no Pin, so the
    // head still holds one square button.
    HomeWidgetAction_close: {
      extends: 'HomeWidgetAction',
      dataBoardDrillClose: true,
      if: (el, s) => !!(s && s.kind === 'records' && s.drill),
      ariaLabel: (el, s) =>
        String(el.call('polyglot', 'workspace.assistantRecords.drillClose')).replace(
          '{title}',
          (s && s.title) || ''
        ),
      onClick: (e, el, s) => {
        e.stopPropagation()
        el.call('assistantBoardDrillClose', s && s.key)
      },
      Icon: { name: 'x' }
    }
  },

  // The mount host: a module body (with its params), the block renderer, or a
  // Fly UI section's result (the chat's own answer-UI renderers) once it is in.
  CardBody: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    minWidth: '0',
    minHeight: '0',
    isRecords: (el, s) => !!s && s.kind === 'records',
    '.isRecords': { overflowY: 'auto', overscrollBehavior: 'contain' },
    // A read's rows can outgrow the card: they scroll inside it.
    isUi: (el, s) => !!s && s.kind === 'ui',
    '.isUi': { overflowY: 'auto', overscrollBehavior: 'contain' },
    if: (el, s) => !!s && s.kind !== 'skeleton',
    hide: (el, s) =>
      !s ||
      (s.kind === 'block' && !!s.empty) ||
      (s.kind === 'ui' && el.call('assistantFlyUiState', s) !== 'ready'),
    onRender: (el, s) => el.call('assistantBoardMountBody', el, s),
    onUpdate: (el, s) => el.call('assistantBoardMountBody', el, s),
    // A Fly UI section's result lands on the SAME card (`fly.ui` — the card
    // keeps its key): a state value of the card, so its own selector — a
    // parent's write never runs this element's onUpdate.
    stateDeps: [(el, s) => (s && s.kind === 'ui' ? s.ui || null : null)],
    onStateUpdate: (el, s) => el.call('assistantBoardMountBody', el, s)
  },

  // Skeleton: a hero bar and a visual block at the body's place — also while a
  // Fly UI section's read is out, with the line that says what it reads.
  CardSkeleton: {
    if: (el, s) => !!s && (s.kind === 'skeleton' || s.kind === 'ui'),
    hide: (el, s) =>
      !s ||
      (s.kind !== 'skeleton' &&
        !(s.kind === 'ui' && el.call('assistantFlyUiState', s) === 'reading')),
    flex: '1',
    flow: 'y',
    gap: 'Z',
    minHeight: '0',
    Skeleton_1: { width: '40%', height: 'B' },
    Skeleton_2: { flex: '1', minHeight: 'B', round: 'radiusControl' },
    CardReading: {
      tag: 'p',
      dataFlyReading: true,
      if: (el, s) => !!s && s.kind === 'ui',
      role: 'status',
      margin: '0',
      fontSize: 'Z',
      color: 'caption',
      overflowWrap: 'anywhere',
      text: (el, s) => el.call('assistantFlyUiNote', s)
    }
  },

  // A block with no data: one sentence WITH its params and the board's first
  // refine suggestion as the one quiet action — never a zero, never a tall
  // empty card. A Fly UI section whose read found nothing or did not come
  // back says its one sentence here too (functions/assistantFlyUi.js).
  HomeWidgetEmpty: {
    if: (el, s) => !!s && (s.kind === 'block' || s.kind === 'ui'),
    hide: (el, s) => {
      if (!s) return true
      if (s.kind === 'ui') {
        const state = el.call('assistantFlyUiState', s)
        return state !== 'empty' && state !== 'error'
      }
      return !s.empty
    },
    flex: '1',
    Text: {
      text: (el, s) => {
        if (s && s.kind === 'ui') return el.call('assistantFlyUiNote', s)
        return s && s.empty ? el.call('assistantBoardEmptyItem', s) : ''
      }
    },
    Action: {
      text: (el, s) => {
        const vm = el.call('assistantBoardViewModel', s && s.root)
        const first = vm && vm.spec && (vm.spec.refine || [])[0]
        return (first && first.label) || ''
      },
      onClick: (e, el, s) => {
        const vm = el.call('assistantBoardViewModel', s && s.root)
        const first = vm && vm.spec && (vm.spec.refine || [])[0]
        if (first) el.call('assistantBoardRefine', first)
      }
    }
  },

  // A block says where its numbers came from (spec §2); a widget body carries
  // its own footer.
  HomeWidgetFooter: {
    if: (el, s) => !!s && (s.kind === 'block' || s.kind === 'records'),
    alignSelf: 'flex-start',
    marginTop: 'auto',
    text: (el, s) =>
      s && (s.kind === 'block' || s.kind === 'records')
        ? el.call('assistantBoardBlockFooter', s)
        : ''
  }
}
